import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Check, X } from 'lucide-react';
import { useTicketApi } from '../lib/ticketApi';

const usd = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Bet amount + potential payout, editable in place after the ticket is uploaded.
export default function TicketMoney({ ticket, onChanged, compact = false }) {
  const api = useTicketApi();
  const [editing, setEditing] = useState(false);
  const [stake, setStake] = useState('');
  const [pay, setPay] = useState('');
  const open = () => { setStake(ticket.stake ?? ''); setPay(ticket.potential_payout ?? ''); setEditing(true); };
  const save = async () => {
    if (stake === '' || pay === '' || Number(stake) < 0 || Number(pay) < 0) { toast.error('Enter a bet and payout of $0 or more'); return; }
    try {
      await axios.patch(`${api.base}/parlays/${ticket.id}/money`, {
        stake: stake === '' ? null : Number(stake), potential_payout: pay === '' ? null : Number(pay),
      }, { headers: api.headers });
      setEditing(false);
      toast.success('Bet and payout updated');
      onChanged();
      window.dispatchEvent(new Event('ticket-money-changed'));
    } catch (e) { toast.error(`Couldn't save: ${e.response?.data?.detail || e.message}`); }
  };
  const txt = compact ? 'text-[10px]' : 'text-sm';
  const inp = `rounded bg-zinc-950 border border-zinc-700 px-1.5 py-0.5 text-white ${compact ? 'w-16 text-[11px]' : 'w-20 text-sm'}`;
  if (editing) {
    return (
      <div className="flex items-center gap-1 flex-wrap relative z-20" data-testid={`money-edit-${ticket.id}`}>
        <span className={`${txt} text-zinc-400`}>Bet $</span>
        <input type="number" inputMode="decimal" value={stake} onChange={(e) => setStake(e.target.value)} className={inp} data-testid={`stake-input-${ticket.id}`} />
        <span className={`${txt} text-zinc-400`}>Pays $</span>
        <input type="number" inputMode="decimal" value={pay} onChange={(e) => setPay(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} className={inp} data-testid={`payout-input-${ticket.id}`} />
        <button type="button" onClick={save} className="text-emerald-300" title="Save" data-testid={`money-save-${ticket.id}`}><Check className="w-4 h-4" /></button>
        <button type="button" onClick={() => setEditing(false)} className="text-zinc-500" title="Cancel" data-testid={`money-cancel-${ticket.id}`}><X className="w-4 h-4" /></button>
      </div>
    );
  }
  if (compact) {
    return (
      <p className="text-[10px] text-zinc-400 flex items-center gap-1 relative z-20" data-testid={`money-${ticket.id}`}>
        <span data-testid={`stake-${ticket.id}`}>{usd(ticket.stake)}</span> → <span className="text-emerald-300 font-semibold" data-testid={`payout-${ticket.id}`}>{usd(ticket.potential_payout)}</span>
        <button type="button" onClick={open} className="text-zinc-500 hover:text-amber-300" title="Edit bet / payout" data-testid={`money-edit-btn-${ticket.id}`}><Pencil className="w-3 h-3" /></button>
      </p>
    );
  }
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid={`money-${ticket.id}`}>
      <span className="text-sm text-zinc-400">Bet <span className="text-white font-bold text-base" data-testid={`stake-${ticket.id}`}>{ticket.stake ? usd(ticket.stake) : '—'}</span></span>
      <span className="text-sm text-zinc-400">Pays <span className="text-emerald-300 font-bold text-base" data-testid={`payout-${ticket.id}`}>{ticket.potential_payout ? usd(ticket.potential_payout) : '—'}</span></span>
      <button type="button" onClick={open} className="text-zinc-500 hover:text-amber-300" title="Edit bet / payout" data-testid={`money-edit-btn-${ticket.id}`}><Pencil className="w-3.5 h-3.5" /></button>
    </div>
  );
}
